// src/services/stars.js
//
// 🟢 STARRED MESSAGES — the same store the phone uses, so sync costs nothing.
//
// The app keeps stars in Firestore, one doc per star:
//   users/{uid}/starredMessages/{chatId}__{messageId} = { chatId, messageId, starredAt }
// (ChatModal.tsx:1009). NOT a field on the message doc — a message doc is often already
// swept from the cloud by the time anyone stars it, and a star is the STARRER's private
// business anyway; the other person's copy of the message must not change.
//
// Because both clients read and write this one subcollection, a star made here appears
// in the app's Starred screen on the next snapshot, and vice versa. Nothing to sync.

import {
  collection, doc, setDoc, deleteDoc, onSnapshot, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';

const starDocId = (chatId, messageId) => `${chatId}__${messageId}`;

/**
 * Live map of everything this account has starred.
 * cb(Map<chatId, Set<messageId>>) — same shape as the app's in-memory starredByChat.
 */
export function watchStarred(uid, cb) {
  if (!uid) return () => {};
  return onSnapshot(collection(db, 'users', uid, 'starredMessages'), (snap) => {
    const byChat = new Map();
    snap.forEach((d) => {
      const v = d.data() || {};
      if (!v.chatId || !v.messageId) return;
      if (!byChat.has(v.chatId)) byChat.set(v.chatId, new Set());
      byChat.get(v.chatId).add(v.messageId);
    });
    cb(byChat);
  }, () => cb(new Map()));
}

export async function starMessage(uid, chatId, messageId) {
  await setDoc(doc(db, 'users', uid, 'starredMessages', starDocId(chatId, messageId)), {
    chatId, messageId, starredAt: serverTimestamp(),
  });
}

export async function unstarMessage(uid, chatId, messageId) {
  await deleteDoc(doc(db, 'users', uid, 'starredMessages', starDocId(chatId, messageId)));
}

export function toggleStar(uid, chatId, messageId, isStarred) {
  return isStarred ? unstarMessage(uid, chatId, messageId) : starMessage(uid, chatId, messageId);
}
